import React, { Component } from "react";
import {
  DateRangePicker,
  toFixed,
  format_date,
  GET,
  loading,
  Navbar,
  XLSX,
  saveAs,
} from "../../components/CustomComponent.js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

// สีประจำ gateway บนกราฟ (วนใช้ซ้ำถ้ามีมากกว่านี้)
const GATEWAY_COLORS = [
  "212, 175, 55",
  "20, 184, 166",
  "245, 158, 11",
  "99, 102, 241",
  "244, 63, 94",
  "14, 165, 233",
  "132, 204, 22",
  "168, 85, 247",
];

// จำกัดช่วงค้นหาไม่เกิน 60 วัน (นับรวมวันเริ่มและวันสิ้นสุด) ตรงกับที่ backend ตรวจ
const MAX_RANGE_DAYS = 60;
const inclusiveDays = (start, end) => {
  const s = new Date(start), e = new Date(end);
  const a = Date.UTC(s.getFullYear(), s.getMonth(), s.getDate());
  const b = Date.UTC(e.getFullYear(), e.getMonth(), e.getDate());
  return Math.round((b - a) / 86400000) + 1;
};

const GATEWAY_COLUMNS = [
  "Gateway", "Gateway Code", "Currencies",
  "Deposit Orders", "Deposit Amount", "Deposit Cost (ex VAT)", "Deposit Cost VAT", "Deposit Cost",
  "Withdrawal Orders", "Withdrawal Amount", "Withdrawal Cost (ex VAT)", "Withdrawal Cost VAT", "Withdrawal Cost",
  "Total Orders", "Total Amount", "Total Cost", "Cost Ratio %", "Cost Share %", "Volume Share %",
];

export default class GatewayReports extends Component {
  constructor(props) {
    super(props);
    const now = new Date();
    this.state = {
      filter_start_date: new Date(now.getFullYear(), now.getMonth(), 1),
      filter_end_date: now,
      filter_currency: "THB",
      chart_side: "all", // all | deposit | withdrawal
      report: null,
      loaded: false,
    };
  }

  async componentDidMount() {
    ChartJS.register(
      CategoryScale,
      LinearScale,
      PointElement,
      LineElement,
      BarElement,
      Title,
      Tooltip,
      Legend
    );
    loading(false);
    await this.Get();
  }

  Get = async () => {
    const { filter_start_date, filter_end_date, filter_currency } = this.state;
    if (!filter_start_date || !filter_end_date) return;
    if (inclusiveDays(filter_start_date, filter_end_date) > MAX_RANGE_DAYS) return;
    loading(true);
    const result = await GET("v1/admin/profits/gateway-report", {
      start_date: format_date(filter_start_date, "yyyy-mm-dd", "en"),
      end_date: format_date(filter_end_date, "yyyy-mm-dd", "en"),
      currency: filter_currency,
    });
    this.setState({
      report: result?.status ? result.data : null,
      loaded: true,
    });
    loading(false);
  };

  BuildGatewayRows = () => {
    const { report } = this.state;
    const rows = (report?.gateways || []).map((g) => [
      g.gateway_name,
      g.gateway,
      (g.currencies || []).join("+"),
      g.deposit.orders, g.deposit.amount, g.deposit.cost_gateway, g.deposit.cost_vat, g.deposit.cost,
      g.withdrawal.orders, g.withdrawal.amount, g.withdrawal.cost_gateway, g.withdrawal.cost_vat, g.withdrawal.cost,
      g.total.orders, g.total.amount, g.total.cost, g.cost_ratio, g.cost_share, g.volume_share,
    ]);
    const t = report?.total;
    if (t) {
      rows.push([
        "TOTAL", "", "",
        t.deposit.orders, t.deposit.amount, t.deposit.cost_gateway, t.deposit.cost_vat, t.deposit.cost,
        t.withdrawal.orders, t.withdrawal.amount, t.withdrawal.cost_gateway, t.withdrawal.cost_vat, t.withdrawal.cost,
        t.total.orders, t.total.amount, t.total.cost, t.cost_ratio, 100, 100,
      ]);
    }
    return rows;
  };

  FileSuffix = () => {
    const { report, filter_currency } = this.state;
    return `${report?.start_date || ""}_${report?.end_date || ""}-${filter_currency}`;
  };

  ExportCSV = () => {
    const rows = this.BuildGatewayRows();
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [GATEWAY_COLUMNS, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `gateway-report-${this.FileSuffix()}.csv`);
  };

  ExportExcel = () => {
    const { report } = this.state;
    const workbook = XLSX.utils.book_new();
    // Sheet 1: สรุปราย gateway
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet([GATEWAY_COLUMNS, ...this.BuildGatewayRows()]),
      "Gateways"
    );
    // Sheet 2: รายวัน x gateway (cost)
    const gateways = report?.gateways || [];
    const dailyHeader = [
      "Date", "Orders", "Amount", "Cost", "Deposit Cost", "Withdrawal Cost",
      ...gateways.map((g) => `${g.gateway_name} Cost`),
      ...gateways.map((g) => `${g.gateway_name} Deposit Cost`),
      ...gateways.map((g) => `${g.gateway_name} Withdrawal Cost`),
      ...gateways.map((g) => `${g.gateway_name} Amount`),
    ];
    const dailyRows = (report?.daily || []).map((d) => [
      d.date, d.orders, d.amount, d.cost, d.deposit?.cost || 0, d.withdrawal?.cost || 0,
      ...gateways.map((g) => d.gateways?.[g.gateway]?.cost || 0),
      ...gateways.map((g) => d.gateways?.[g.gateway]?.deposit?.cost || 0),
      ...gateways.map((g) => d.gateways?.[g.gateway]?.withdrawal?.cost || 0),
      ...gateways.map((g) => d.gateways?.[g.gateway]?.amount || 0),
    ]);
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet([dailyHeader, ...dailyRows]),
      "Daily"
    );
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      `gateway-report-${this.FileSuffix()}.xlsx`
    );
  };

  render() {
    const {
      filter_start_date,
      filter_end_date,
      filter_currency,
      chart_side,
      report,
      loaded,
    } = this.state;

    const cell = (v) => toFixed(v, 2);
    const num = (v) => Number(v) || 0;
    const cnt = (v) => num(v).toLocaleString();
    const pct = (v) => `${toFixed(v, 2)}%`;
    const currency = global.currency?.find((c) => c.currency_code === filter_currency);
    const currencySymbol =
      filter_currency === "THB" ? "฿" : filter_currency === "LAK" ? "₭" : "";

    const gateways = report?.gateways || [];
    const total = report?.total;
    const daily = report?.daily || [];
    // กราฟเลือกฝั่งได้: all ใช้ยอดรวม, deposit/withdrawal ใช้ยอดของฝั่งนั้น
    const sideKey = chart_side === "deposit" || chart_side === "withdrawal" ? chart_side : null;
    const sideOf = (obj) => (sideKey ? obj?.[sideKey] : obj) || {};
    const dailyCost = (d, code) => num(sideOf(code ? d.gateways?.[code] : d).cost);
    const chartGateways = gateways.filter((g) => num((sideKey ? g[sideKey] : g.total).cost) > 0);
    const activeCount = gateways.filter((g) => num(g.total.orders) > 0).length;
    const sideLabel = sideKey === "deposit" ? "Deposit" : sideKey === "withdrawal" ? "Withdrawal" : "Deposit + Withdrawal";
    const rangeLabel =
      filter_start_date && filter_end_date
        ? `${format_date(filter_start_date, "dd/mm/yyyy", "en")} – ${format_date(filter_end_date, "dd/mm/yyyy", "en")}`
        : "Select date range";

    const kpis = total
      ? [
          {
            label: "Total Volume",
            value: cell(total.total.amount),
            sub: `${num(total.total.orders).toLocaleString()} orders · ${activeCount}/${gateways.length} gateway${gateways.length === 1 ? "" : "s"} with data`,
            accent: "#6366f1",
            icon: "",
          },
          {
            label: "Gateway Cost",
            value: cell(total.total.cost),
            sub: `ex VAT ${cell(total.total.cost_gateway)} · VAT ${cell(total.total.cost_vat)}`,
            accent: "#f59e0b",
            icon: "",
            negative: false,
          },
          {
            label: "Cost Ratio",
            value: pct(total.cost_ratio),
            sub: `Avg cost / order ${cell(total.total.orders ? total.total.cost / total.total.orders : 0)}`,
            accent: "#14b8a6",
            icon: "",
            plain: true,
          },
          (() => {
            const top = gateways.find((g) => num(g.total.cost) > 0);
            return {
              label: "Highest Cost Gateway",
              value: top ? top.gateway_name : "—",
              sub: top
                ? `${currencySymbol}${cell(top.total.cost)} · ${toFixed(top.cost_share, 2)}% of total cost`
                : "No gateway cost in this period",
              accent: "#d4af37",
              icon: "\uf091",
              plain: true,
              highlight: true,
            };
          })(),
        ]
      : [];

    const chartData = {
      labels: daily.map((d) => d.date.slice(5)),
      datasets: chartGateways.map((g) => ({
        id: g.gateway,
        label: g.gateway_name,
        data: daily.map((d) => dailyCost(d, g.gateway)),
        backgroundColor: `rgba(${GATEWAY_COLORS[gateways.indexOf(g) % GATEWAY_COLORS.length]}, 0.85)`,
        borderColor: `rgba(${GATEWAY_COLORS[gateways.indexOf(g) % GATEWAY_COLORS.length]}, 1)`,
        borderWidth: 1,
        borderRadius: 4,
        stack: "cost",
      })),
    };
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${toFixed(ctx.parsed.y, 2)}`,
            footer: (items) =>
              `Total: ${toFixed(items.reduce((s, it) => s + (it.parsed.y || 0), 0), 2)}`,
          },
        },
      },
      scales: {
        x: { stacked: true, grid: { display: false } },
        y: {
          stacked: true,
          grid: { color: "rgba(148,163,184,0.15)" },
          ticks: { callback: (v) => Number(v).toLocaleString() },
        },
      },
    };

    return (
      <div className="w-100 min-vh-100 premium-report">
        <Navbar />
        <div className="body p-3 mt-3">
          {/* ===== Hero ===== */}
          <div className="ar-hero mb-4">
            <div className="ar-hero-head">
              <div>
                <div className="ar-eyebrow mb-1">Gateway Report</div>
                <div className="ar-title">Gateway Cost Report</div>
                <div className="ar-subtitle">
                  Volume and gateway cost per payment gateway for the selected period
                </div>
              </div>
              <div className="ar-hero-actions">
                <button className="ar-btn-ghost gateway-reports.read" onClick={this.ExportCSV}>
                  <span className="icon me-2">{""}</span>Export CSV
                </button>
                <button className="ar-btn-ghost gateway-reports.read" onClick={this.ExportExcel}>
                  <span className="icon me-2">{""}</span>Export Excel
                </button>
              <div className="text-end">
                <div className="ar-year">
                  {report?.days ?? "—"}
                  <span className="ar-year-small"> day{report?.days === 1 ? "" : "s"}</span>
                </div>
                <div className="ar-year-caption">
                  <span className="ar-caption-chip ms-2">{rangeLabel}</span>
                </div>
              </div>
              </div>
            </div>

            <div className="ar-toolbar ar-filter mt-3">
<div className="ar-filter-head">
<span className="ar-toolbar-label"><span className="icon me-2">{""}</span>Filter</span>
<span className="ar-toolbar-hint">max {MAX_RANGE_DAYS} days</span>
</div>
<div className="ar-filter-row">
<div className="ar-filter-field">
<span className="ar-filter-label">Date Range</span>
<DateRangePicker
                    className="form-control"
                    value={{ start_date: filter_start_date, end_date: filter_end_date }}
                    maxRangeDays={MAX_RANGE_DAYS}
                    onChange={(e) => {
                      this.setState(
                        {
                          filter_start_date: e.start_date,
                          filter_end_date: e.end_date,
                        },
                        () => {
                          if (e.start_date && e.end_date) this.Get();
                        }
                      );
                    }}
                  />
</div>
</div>
<div className="ar-filter-foot">
<div className="d-flex align-items-center gap-2">
                  {global.currency?.map((item) => (
                    <button
                      key={item.currency_code}
                      className={`ar-pill ${item.currency_code === filter_currency ? "active" : ""}`}
                      onClick={() => {
                        this.setState({ filter_currency: item.currency_code }, () => this.Get());
                      }}
                    >
                      {item.image}
                      <span className="ms-1">{item.currency_code}</span>
                    </button>
                  ))}
                </div>
</div>
</div>
          </div>

          {/* ===== KPI ===== */}
          {kpis.length > 0 && (
            <div className="row g-3 mb-4">
              {kpis.map((k) => (
                <div className="col-12 col-sm-6 col-xl-3" key={k.label}>
                  <div
                    className={`ar-kpi ${k.highlight ? "ar-kpi-highlight" : ""}`}
                    style={{ "--ar-accent": k.accent }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="ar-kpi-label">{k.label}</div>
                      <div className="ar-kpi-icon">
                        <span className="icon">{k.icon}</span>
                      </div>
                    </div>
                    <div className={`ar-kpi-value ${k.negative ? "ar-neg" : ""}`}>
                      {!k.plain && <span className="ar-kpi-currency">{currencySymbol}</span>}
                      {k.value}
                    </div>
                    <div className="ar-kpi-sub">{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ===== Daily cost chart ===== */}
          {daily.length > 0 && gateways.some((g) => num(g.total.cost) > 0) && (
            <div className="ar-table-card mb-4">
              <div className="ar-table-head flex-wrap gap-2">
                <div>
                  <div className="ar-table-title">Daily Gateway Cost</div>
                  <div className="ar-table-caption">
                    {sideLabel} · stacked by gateway · {filter_currency} · cost includes VAT
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="d-none d-md-block ar-table-caption">
                    Peak day:{" "}
                    {(() => {
                      const peak = daily.reduce(
                        (a, b) => (dailyCost(b) > dailyCost(a) ? b : a),
                        daily[0]
                      );
                      return `${peak.date} · ${cell(dailyCost(peak))}`;
                    })()}
                  </div>
                  <div className="ar-seg">
                    {[
                      { key: "all", label: "All" },
                      { key: "deposit", label: "Deposit" },
                      { key: "withdrawal", label: "Withdrawal" },
                    ].map((o) => (
                      <button
                        key={o.key}
                        className={`ar-seg-btn ${chart_side === o.key ? "active" : ""} ${o.key !== "all" ? `ar-seg-${o.key}` : ""}`}
                        onClick={() => this.setState({ chart_side: o.key })}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ar-chart">
                {chartGateways.length > 0 ? (
                  <Bar datasetIdKey="id" options={chartOptions} data={chartData} />
                ) : (
                  <div className="ar-empty h-100 d-flex flex-column align-items-center justify-content-center">
                    <span className="icon d-block mb-2">{"\uf080"}</span>
                    No {sideLabel.toLowerCase()} cost in this period
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ===== Gateway table ===== */}
          <div className="ar-table-card">
            <div className="ar-table-head">
              <div>
                <div className="ar-table-title">Gateway Breakdown</div>
                <div className="ar-table-caption">
                  {rangeLabel} · {gateways.length} active {filter_currency} gateway{gateways.length === 1 ? "" : "s"} · sorted by volume
                </div>
              </div>
              <div className="d-none d-md-flex align-items-center gap-3 ar-legend">
                <span><i className="ar-legend-dot ar-legend-dep"></i>Deposit</span>
                <span><i className="ar-legend-dot ar-legend-wd"></i>Withdrawal</span>
                <span><i className="ar-legend-dot ar-legend-sum"></i>Summary</span>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-hover text-nowrap ar-table">
                <thead>
                  <tr>
                    <td rowSpan={2} className="align-middle ar-sticky">Gateway</td>
                    <td colSpan={3} className="text-center ar-group ar-group-dep ar-group-start">
                      <span className="icon me-2">{""}</span>Deposit
                    </td>
                    <td colSpan={3} className="text-center ar-group ar-group-wd ar-group-start">
                      <span className="icon me-2">{""}</span>Withdrawal
                    </td>
                    <td colSpan={4} className="text-center ar-group ar-group-sum ar-group-start">
                      <span className="icon me-2">{""}</span>Summary
                    </td>
                  </tr>
                  <tr>
                    <td className="text-center ar-group-start">Orders</td>
                    <td className="text-end">Amount</td>
                    <td className="text-end ar-head-profit" title="cost_gateway + VAT">Cost</td>
                    <td className="text-center ar-group-start">Orders</td>
                    <td className="text-end">Amount</td>
                    <td className="text-end ar-head-profit" title="cost_gateway + VAT">Cost</td>
                    <td className="text-end ar-group-start ar-head-profit">Total Cost</td>
                    <td className="text-end" title="Total cost ÷ total amount">Cost %</td>
                    <td className="text-end" title="Share of total gateway cost">Cost Share</td>
                    <td className="text-end" title="Share of total volume">Vol. Share</td>
                  </tr>
                </thead>
                <tbody>
                  {gateways.map((g, i) => (
                    <tr key={g.gateway} className={num(g.total.orders) === 0 ? "ar-row-empty" : ""}>
                      <td className="ar-sticky">
                        <div className="ar-gateway">
                          <span
                            className="ar-gateway-dot"
                            style={{ background: `rgb(${GATEWAY_COLORS[i % GATEWAY_COLORS.length]})` }}
                          ></span>
                          <div>
                            <div className="ar-gateway-meta">
                              {g.gateway}
                              {(g.currencies || []).map((c) => (
                                <span key={c} className={`ar-chip-cur ms-2 ${c === filter_currency ? "active" : ""}`}>
                                  {c}
                                </span>
                              ))}
                              {num(g.total.orders) === 0 && (
                                <span className="ar-chip-empty ms-2">no data</span>
                              )}
                              {g.is_active === false && (
                                <span className="ar-chip-inactive ms-2">inactive</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center ar-group-start">{cnt(g.deposit.orders)}</td>
                      <td className="text-end">{cell(g.deposit.amount)}</td>
                      <td className="text-end ar-dep-profit">{cell(g.deposit.cost)}</td>
                      <td className="text-center ar-group-start">{cnt(g.withdrawal.orders)}</td>
                      <td className="text-end">{cell(g.withdrawal.amount)}</td>
                      <td className="text-end ar-wd-profit">{cell(g.withdrawal.cost)}</td>
                      <td className="text-end ar-group-start ar-sum-cost">{cell(g.total.cost)}</td>
                      <td className="text-end">
                        <span className={`ar-ratio ${g.cost_ratio > (total?.cost_ratio || 0) ? "ar-ratio-high" : ""}`}>
                          {pct(g.cost_ratio)}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="ar-share">
                          <div className="ar-share-bar ar-share-bar-cost">
                            <span style={{ width: `${Math.min(100, g.cost_share)}%` }}></span>
                          </div>
                          <span className="ar-share-val">{pct(g.cost_share)}</span>
                        </div>
                      </td>
                      <td className="text-end">
                        <div className="ar-share">
                          <div className="ar-share-bar">
                            <span style={{ width: `${Math.min(100, g.volume_share)}%` }}></span>
                          </div>
                          <span className="ar-share-val">{pct(g.volume_share)}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {total && gateways.length > 0 && (
                    <tr className="ar-total">
                      <td className="ar-sticky">
                        <div className="ar-month-badge">
                          <span className="ar-month-badge-total">Σ</span>
                          TOTAL
                        </div>
                      </td>
                      <td className="text-center ar-group-start">{cnt(total.deposit.orders)}</td>
                      <td className="text-end">{cell(total.deposit.amount)}</td>
                      <td className="text-end">{cell(total.deposit.cost)}</td>
                      <td className="text-center ar-group-start">{cnt(total.withdrawal.orders)}</td>
                      <td className="text-end">{cell(total.withdrawal.amount)}</td>
                      <td className="text-end">{cell(total.withdrawal.cost)}</td>
                      <td className="text-end ar-group-start">{cell(total.total.cost)}</td>
                      <td className="text-end">{pct(total.cost_ratio)}</td>
                      <td className="text-end">100.00%</td>
                      <td className="text-end">100.00%</td>
                    </tr>
                  )}
                  {loaded && gateways.length === 0 && (
                    <tr>
                      <td colSpan={11} className="text-center py-5">
                        <div className="ar-empty">
                          <span className="icon d-block mb-2">{""}</span>
                          No transactions for {rangeLabel} · {filter_currency}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
